/**
 * Experiment Observability Service
 *
 * Tracks experiment decisions for debugging and monitoring.
 * Provides layer-aware logging that can be enabled per-experiment.
 * 
 * Pure service with no React dependencies.
 */

import { AllowedExperimentKey, TenantId } from '@tenants/types';
import { getTenantById } from '@tenants';
import { isProduction } from 'utils/environment';
import { CircuitBreakerLayer } from 'services/experiment-circuit-breaker';
import {
  ExperimentContext,
  ExperimentReason,
  EXPERIMENT_DECISION,
  EXPERIMENT_VIEWED,
  EXPERIMENT_URL_OVERRIDE,
} from '../constants/experiment-events';
import { trackEvent } from '../utils/event-tracker';

/**
 * Check if observability is enabled for a specific experiment in a tenant's config.
 */
function isObservabilityEnabled(experimentKey: AllowedExperimentKey, tenantId?: TenantId): boolean {
  if (!tenantId) return false;
  
  const tenant = getTenantById(tenantId);
  const experimentConfig = tenant.features.Experiments?.[experimentKey];
  
  return experimentConfig?.observability ?? false;
}

/**
 * Parameters for tracking experiment decisions
 */
export interface ExperimentDecisionParams {
  experimentKey: AllowedExperimentKey;
  result: boolean;
  layer: CircuitBreakerLayer;
  context: ExperimentContext;
  reason: string;
  tenantId?: TenantId;
  metadata?: Record<string, any>;
}

/**
 * Parameters for tracking tenant config blocks
 */
export interface TenantConfigBlockParams {
  experimentKey: AllowedExperimentKey;
  context: ExperimentContext;
  tenantId?: TenantId;
  enabledExperiments?: AllowedExperimentKey[];
}

/**
 * Parameters for tracking payload condition blocks
 */
export interface PayloadConditionBlockParams {
  experimentKey: AllowedExperimentKey;
  context: ExperimentContext;
  tenantId?: TenantId;
  payloadConditions?: any[];
}

/**
 * Parameters for tracking overrides (ModHeader/URL)
 */
export interface OverrideParams {
  experimentKey: AllowedExperimentKey;
  context: ExperimentContext;
  result: boolean;
  tenantId?: TenantId;
}

/**
 * Parameters for tracking GrowthBook evaluations
 */
export interface GrowthBookEvaluationParams {
  experimentKey: AllowedExperimentKey;
  context: ExperimentContext;
  result: boolean;
  tenantId?: TenantId;
  variationId?: number;
  inExperiment?: boolean;
  hashUsed?: boolean;
}

/**
 * Experiment Observability Service
 * 
 * Provides centralized logging and tracking for experiment decisions.
 * Logging can be enabled/disabled per-experiment via configuration.
 */
export const ExperimentObservabilityService = {
  /**
   * Track an experiment decision with layer and context information.
   * Only tracks if observability is enabled for the experiment in tenant config.
   * Works in all environments (dev, staging, production).
   */
  trackDecision(params: ExperimentDecisionParams): void {
    const { experimentKey, result, layer, context, reason, tenantId, metadata = {}} = params;
    
    // Check if observability is enabled for this experiment in tenant config
    if (!isObservabilityEnabled(experimentKey, tenantId)) {
      return;
    }

    // Console logging (development only for debugging)
    if (!isProduction) {
      const message = `[GrowthBook:${context}:${layer}] ${experimentKey} = ${result}`;
      console.debug(message, { reason, tenantId, ...metadata });
    }

    // Track event for monitoring (all environments - NewRelic in prod, console in dev)
    trackEvent(EXPERIMENT_DECISION, {
      experimentKey,
      result,
      layer,
      context,
      reason,
      tenantId,
      ...metadata,
    });
  },

  /**
   * Track when tenant config blocks an experiment
   */
  trackTenantConfigBlocked(params: TenantConfigBlockParams): void {
    const { experimentKey, context, tenantId, enabledExperiments } = params;
    
    this.trackDecision({
      experimentKey,
      result: false,
      layer: CircuitBreakerLayer.TENANT_CONFIG,
      context,
      reason: ExperimentReason.BLOCKED_BY_TENANT_CONFIG,
      tenantId,
      metadata: { enabledExperiments },
    });
  },

  /**
   * Track when payload condition blocks an experiment
   */
  trackPayloadConditionBlocked(params: PayloadConditionBlockParams): void {
    const { experimentKey, context, tenantId, payloadConditions } = params;
    
    this.trackDecision({
      experimentKey,
      result: false,
      layer: CircuitBreakerLayer.PAYLOAD_CONDITION,
      context,
      reason: ExperimentReason.BLOCKED_BY_PAYLOAD_CONDITION,
      tenantId,
      metadata: { payloadConditions },
    });
  },

  /**
   * Track when an override (ModHeader/URL) is applied
   */
  trackOverrideApplied(params: OverrideParams): void {
    const { experimentKey, context, result, tenantId } = params;
    
    this.trackDecision({
      experimentKey,
      result,
      layer: CircuitBreakerLayer.OVERRIDE,
      context,
      reason: ExperimentReason.MODHEADER_OVERRIDE,
      tenantId,
    });
  },

  /**
   * Track GrowthBook evaluation result
   */
  trackGrowthBookEvaluation(params: GrowthBookEvaluationParams): void {
    const { experimentKey, context, result, tenantId, variationId, inExperiment, hashUsed } = params;
    
    this.trackDecision({
      experimentKey,
      result,
      layer: CircuitBreakerLayer.GROWTHBOOK_EVALUATION,
      context,
      reason: result ? ExperimentReason.USER_IN_VARIATION : ExperimentReason.USER_IN_CONTROL,
      tenantId,
      metadata: { variationId, inExperiment, hashUsed },
    });
  },

  /**
   * Track experiment view (analytics event).
   * Always tracks regardless of observability config (for analytics).
   */
  trackExperimentViewed(experimentKey: AllowedExperimentKey, variationId: number, tenantId: TenantId): void {
    // Console logging (development only)
    if (!isProduction) {
      console.debug('[GrowthBook:Experiment] Viewed Experiment', {
        experimentId: experimentKey,
        variationId,
        tenantId,
      });
    }

    // Always track analytics event (all environments)
    trackEvent(EXPERIMENT_VIEWED, {
      experimentId: experimentKey,
      variationId,
    }, tenantId);
  },

  /**
   * Track URL override initialization.
   * Tracks in all environments for security monitoring.
   */
  trackUrlOverrideInit(experimentKey: string, value: number, environment: string): void {
    // Console logging (development only)
    if (!isProduction) {
      console.debug(
        `[GrowthBook:URLOverride] ${experimentKey} = ${value} (allowed in ${environment} environment)`
      );
    }

    // Track event (all environments - important for security monitoring)
    trackEvent(EXPERIMENT_URL_OVERRIDE, {
      experimentKey,
      value,
      environment,
    });
  },
};
